<script setup lang="ts">
import { computed, ref } from 'vue';
import Panel from 'primevue/panel';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import Button from 'primevue/button';
import ToggleSwitch from 'primevue/toggleswitch';
import {
  type EffectConfig,
  type DisableConfig,
  type RequiredConfig,
  type ConditionConfig,
  type DefaultConfig,
  EFFECT_OPERATORS,
  REQUIRED_VALUES,
  getEffectTitle,
  ButtonType,
  CustomButton 
} from '@/components';
import { FormFieldEffects, FormFieldType } from '@/models';
import { useFormBuilder } from '@/stores/formBuilder';

const builder = useFormBuilder();

const props = defineProps<{
  effectType: FormFieldEffects;
  effect: EffectConfig | DisableConfig | RequiredConfig | DefaultConfig;
}>();
const effect = ref(props.effect);
const inputValues = ref((effect.value as EffectConfig)?.conditions?.map(condition => (condition as ConditionConfig).value));

const collapsed = ref(true);
const whenSelectOptions = ref([
  { label: 'All of these rules', value: 'All' },
  { label: 'Any of these rules', value: 'Or' }
]);

const whenSelectValue = ref(
  whenSelectOptions.value.find(
    option => option.value === (effect.value as EffectConfig).allOr
  )
);

const handleChangeWhen = () => {
  (props.effect as EffectConfig).allOr = whenSelectValue.value!.value;
};

const title = computed(() => getEffectTitle(props.effectType));

const onCollapsed = (collapse: boolean) => {
  collapsed.value = collapse;
};

const getValue = (identifier: string, value: string) => {
  const field = builder.form?.items?.find(
    fieldItem =>
      fieldItem.identifier.toUpperCase() === identifier.toUpperCase()
  );
  if (
    field?.type === FormFieldType.CHOICE ||
    field?.type === FormFieldType.DROPDOWN
  ) {
    return field.options.choices?.find(
      choiceItem => choiceItem.value == value
    )?.label;
  }
  return value;
};

const addNewCondition = () => {
  ((effect.value as EffectConfig).conditions as ConditionConfig[]).push({
    value: '',
    operator: '',
    fieldId: ''
  });
};

const onDeleteCondition = (index: number) => {
  ((effect.value as EffectConfig).conditions as ConditionConfig[]).splice(
    index,
    1
  );
};

const onInputValueChange = (index: number) => {
  ((effect.value as EffectConfig).conditions as ConditionConfig[])[index].value = inputValues.value[index];
};
</script>

<template>
  <Panel toggleable collapsed @update:collapsed="onCollapsed">
    <template #header>
      <div class="panel-header">
        <div class="panel-header-title">
          <FontAwesomeIcon :icon="[title?.iconStyle, title?.icon]"
            class="icon"/>
          <div class="panel-title">{{ title?.title }}</div>
        </div>
        <div class="panel-summary"
          v-if="collapsed && 'allOr' in effect && (effect as EffectConfig)?.conditions.length > 0">
          <h3 class="panel-summary-title">
            {{ (effect as EffectConfig).allOr === "All" ? "All" : "Any" }} of
            these values
          </h3>
          <div v-for="condition in (effect as EffectConfig).conditions"
            :key="(condition as ConditionConfig).value"
            class="summary-conditions">
            {{ (condition as ConditionConfig).formField?.label }}
            <span class="operator-text">{{
              (condition as ConditionConfig).effectOperator?.label
            }}</span>
            {{
              getValue(
                (condition as ConditionConfig).fieldId.replace("@", ""),
                (condition as ConditionConfig).value
              )
            }}
          </div>
          <div class="summary-message" v-if="(effect as EffectConfig).message">
            <FontAwesomeIcon :icon="[`fal`, `arrow-turn-down-right`]"
              class="icon"/>
            <div class="message">{{ (effect as EffectConfig).message }}</div>
          </div>
        </div>
      </div>
    </template>
    <div class="panel-content" v-if="'allOr' in effect">
      <div class="select-all-or">
        <div class="select-label">When</div>
        <Select v-model="whenSelectValue"
          option-label="label"
          @change="handleChangeWhen"
          :options="whenSelectOptions"/>
      </div>
      <div class="rules">
        <div v-for="condition, index in (effect as EffectConfig).conditions"
          :key="(condition as ConditionConfig).value"
          class="rule-row">
          <Select v-model="(condition as ConditionConfig).formField"
            option-label="label"
            class="field-select"
            :options="builder.form?.items"/>
          <Select v-model="(condition as ConditionConfig).effectOperator"
            option-label="label"
            class="operator-select"
            :options="EFFECT_OPERATORS"/>
          <div class="rule-value">
            <InputText v-model="inputValues[index]"
              type="text"
              @change="onInputValueChange(index)"
              v-if="(condition as ConditionConfig).formField?.type !== FormFieldType.CHOICE &&
                (condition as ConditionConfig).formField?.type !== FormFieldType.DROPDOWN"
              class="rule-input"/>
            <Select v-model="(condition as ConditionConfig).choiceValue"
              v-if="(condition as ConditionConfig).formField?.type === FormFieldType.CHOICE ||
                (condition as ConditionConfig).formField?.type === FormFieldType.DROPDOWN"
              option-label="label"
              :options="(condition as ConditionConfig).formField?.options.choices"/>
          </div>
          <Button icon="pi pi-times"
            class="delete-condition"
            outlined
            @click="onDeleteCondition(index)"
            v-ripple/>
        </div>
      </div>

      <CustomButton text="Add another"
        :icon="{ style: 'fas', icon: 'plus' }"
        @onClick="addNewCondition"
        class="add-another-button"
        :type="ButtonType.PRIMARY_WITHOUT_OUTLINE"/>

      <FloatLabel v-if="
          effectType === FormFieldEffects.VALID ||
          effectType === FormFieldEffects.WARN
        "
        class="float-message">
        <InputText id="message" v-model="(effect as EffectConfig).message" />
        <label for="message">Message</label>
      </FloatLabel>
    </div>
    <div class="disable-content" v-if="'disable' in effect">
        <div class="disbale-title">Turn on to disable the field</div>
        <ToggleSwitch v-model="(effect as DisableConfig).disable"/>
    </div>
    <div class="required-content" v-if="'strictness' in effect">
        <Select v-model="(effect as RequiredConfig).required"
          option-label="label"
          class="field-select"
          :options="REQUIRED_VALUES"/>
    </div>
    <div class="default-value-content" v-if="'default' in effect">
      <InputText v-model="(effect as DefaultConfig).default as string"
        type="text"
        class="default-input"/>
    </div>
  </Panel>
</template>

<style scoped lang="scss">
@import "../../../../assets/main.scss";
.panel-header-title {
  display: flex;
  align-items: center;

  .panel-title {
    margin-left: 8px;
  }
}
.panel-summary {
  .panel-summary-title {
    margin-top: 12px;
    padding-bottom: 4px;
    font-weight: 600;
  }

  .operator-text {
    font-weight: 700;
    padding: 0 4px 0 4px;
  }

  .summary-message {
    display: flex;
    align-items: center;

    .message {
      margin-left: 8px;
    }
  }
}
.panel-content {
  .rules {
    margin-top: 24px;
    .rule-row {
      display: flex;
      margin-bottom: 8px;

      .field-select {
        flex: 1.5;
      }

      .operator-select {
        flex: 0.5;
        margin: 0 8px 0 8px;
      }

      .rule-value {
        flex: 1;
        margin-right: 8px;

        .rule-input {
          width: 100%;
        }
      }

      :deep(.p-button-outlined) {
        border: none;
        border-radius: 4px;
        color: black;

        &:not(:disabled):hover {
          border: none;
          background-color: $disabled;
          color: black;
        }
      }
    }
  }

  .float-message {
    margin-top: 28px;
  }

  #message {
    width: 100%;
  }
}
.disable-content {
    display: flex;

    .disbale-title {
        margin-right: 16px;
    }
}
</style>
